import { useEffect, useRef, useState } from 'react';

import { EMPTY_TEXT } from '@/components/views/BeraJob/BeraReportV2/constants';

interface UseDebouncedCommentParams {
	delay: number;
	onSave: (value: string) => void;
}

type UseDebouncedCommentResult = {
	comment: string;
	setComment: (value: string) => void;
	handleCommentChange: (value: string) => void;
	handleCommentBlur: () => void;
};

export function useDebouncedComment({ delay, onSave }: UseDebouncedCommentParams): UseDebouncedCommentResult {
	const [comment, setComment] = useState<string>(EMPTY_TEXT);

	const commentTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const pendingComment = useRef<string | undefined>(undefined);
	const latestSave = useRef(onSave);

	useEffect(() => {
		latestSave.current = onSave;
	});

	useEffect(() => {
		return () => {
			clearTimeout(commentTimer.current);

			if (pendingComment.current === undefined) {
				return;
			}

			latestSave.current(pendingComment.current);
		};
	}, []);

	function cancelPendingSave(): void {
		clearTimeout(commentTimer.current);

		pendingComment.current = undefined;
	}

	function handleCommentChange(value: string): void {
		setComment(value);

		cancelPendingSave();

		pendingComment.current = value;

		commentTimer.current = setTimeout(() => {
			pendingComment.current = undefined;

			onSave(value);
		}, delay);
	}

	function handleCommentBlur(): void {
		cancelPendingSave();

		onSave(comment);
	}

	const result = {
		comment,
		setComment,
		handleCommentChange,
		handleCommentBlur
	};

	return result;
}
